// Renders one group of the schema. Everything above the field — headings,
// dividers, columns — stays with the app, via the `wrapper` element it supplies.

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import FieldRenderer from './FieldRenderer';
import { messageForError } from './texts';
import { descriptorShape } from './fields/fieldPropTypes';

// Stable default: FieldRenderer is memoized on shallow props, so a fresh
// literal here would re-render every field on every form update.
const NO_COMPONENTS = {};

const PassThrough = ({ children }) => children;

PassThrough.propTypes = { children: PropTypes.node.isRequired };

// Renders one group of the schema. Layout above the field — section headings,
// dividers, columns — stays in the app; `wrapper` is the app's per-field element.
const DynamicFormSection = ({
  descriptors,
  form,
  resolveKind,
  readOnly = true,
  texts = undefined,
  jsonEditor = undefined,
  encryptText = undefined,
  maskUntilEdited = false,
  encryptedPlaceholder = '',
  restoreSecureOnErase = false,
  components = NO_COMPONENTS,
  wrapper: Wrapper = PassThrough,
  onHeaderValidate = undefined,
}) => {
  // Header rows report errors under their own input names ('key-0', 'value-0'),
  // so they share one flat map with the schema fields.
  const errorMessages = useMemo(
    () =>
      Object.keys(form.errors).reduce(
        (acc, key) => ({
          ...acc,
          [key]: messageForError(form.errors[key], texts),
        }),
        {},
      ),
    [form.errors, texts],
  );

  return (
    <>
      {descriptors.map((descriptor) => (
        <Wrapper key={descriptor.name}>
          <FieldRenderer
            descriptor={descriptor}
            kind={resolveKind(descriptor)}
            label={form.labelOf(descriptor.name)}
            value={form.values[descriptor.name]}
            warningMessage={errorMessages[descriptor.name]}
            readOnly={readOnly}
            texts={texts}
            onChange={form.setValue}
            onCheckboxChange={form.setCheckbox}
            onBlur={form.blur}
            onEmailBlur={form.blurEmail}
            headerErrors={errorMessages}
            onHeaderAdd={form.headers.add}
            onHeaderRemove={form.headers.remove}
            onHeaderChange={form.headers.change}
            onHeaderValidate={onHeaderValidate}
            jsonEditorOn={Boolean(form.jsonEditorOn[descriptor.name])}
            jsonEditorDisabled={Boolean(
              form.jsonEditorDisabled[descriptor.name],
            )}
            onJsonToggle={form.toggleJsonEditor}
            onJsonDataChange={form.setJsonData}
            jsonEditor={jsonEditor}
            encryptText={encryptText}
            maskUntilEdited={maskUntilEdited}
            encryptedPlaceholder={encryptedPlaceholder}
            restoreSecureOnErase={restoreSecureOnErase}
            components={components}
          />
        </Wrapper>
      ))}
    </>
  );
};

DynamicFormSection.propTypes = {
  descriptors: PropTypes.arrayOf(descriptorShape).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  form: PropTypes.object.isRequired,
  resolveKind: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  texts: PropTypes.object,
  jsonEditor: PropTypes.elementType,
  encryptText: PropTypes.func,
  maskUntilEdited: PropTypes.bool,
  encryptedPlaceholder: PropTypes.string,
  restoreSecureOnErase: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  components: PropTypes.object,
  wrapper: PropTypes.elementType,
  onHeaderValidate: PropTypes.func,
};
export default DynamicFormSection;
